import React from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Formik } from 'formik'
import { startOfDay, endOfDay } from 'date-fns'
import * as Yup from 'yup'

import Api from 'services/Api'
import BaseModal from 'components/Modals/BaseModal'
import { Button, DateTimePicker, Form, Select, Checkbox } from 'components/Forms'
import { ScrollableUserList } from 'components/UI'
import { timeOffStatuses } from 'services/Requests'
import { closeModal } from 'store/actions/modals'
import { Format, Parse, setTime } from 'services/Dates'
import { Alert } from 'ui'
import { currentLang } from 'translations'

import { t } from 'translations'

const TimeOffMultiCreateModal = ({ userIds }) => {
  const dispatch = useDispatch()
  const { timeOffTypes, timeOffTypesById } = useSelector(state => state.resources)
  const { baseDate } = useSelector(state => state.events)

  const onSubmit = async ({ vacationTypeId, requestStatusId, partialDates, start, end }, actions) => {
    let failedIds = []

    let baseTimeOff = {
      vacationTypeId,
      requestStatusId,
      // If partial date is checked, use the time selected by the user, else set to startOf and endOf day
      start: Format(partialDates ? start : startOfDay(Parse(start))),
      end: Format(partialDates ? end : endOfDay(Parse(end)))
    }

    // Save a time off request for each user selected
    for (const userId of userIds) {
      const { success } = await Api.post(`/requests/vacation`, { ...baseTimeOff, userId })
      if (!success) failedIds.push(userId)
    }

    if (failedIds.length) {
      actions.setSubmitting(false)
      actions.setStatus({ type: 'danger', msg: 'manager.timeOffs.MULTI_CREATE_MODAL.SOME_FAILED', failedCount: failedIds.length })
    } else {
      dispatch(closeModal())
    }
  }

  return (
    <BaseModal title="manager.timeOffs.MULTI_CREATE_MODAL" hideButtons>
      <Formik
        validationSchema={Yup.object().shape({
          start: Yup.date().required('manager.timeOffs.form.START_REQUIRED'),
          end: Yup.date()
            .required('manager.timeOffs.form.END_REQUIRED')
            .when('start', {
              // Only validate for End date > Start date when a start date is selected
              is: startDate => startDate,
              then: Yup.date().min(Yup.ref('start'), 'manager.timeOffs.form.END_BEFORE_START')
            })
        })}
        enableReinitialize={true}
        initialValues={{
          vacationTypeId: timeOffTypes[0]._id,
          requestStatusId: 'APPROVED',
          partialDates: true,
          start: setTime(baseDate, timeOffTypes[0].partialDayByDefault ? timeOffTypes[0].defaultStartTime : null || '07:00'),
          end: setTime(baseDate, timeOffTypes[0].partialDayByDefault ? timeOffTypes[0].defaultEndTime : null || '16:00')
        }}
        onSubmit={onSubmit}
      >
        {({ status, isSubmitting, values, ...actions }) => {
          return (
            <Form resetForm={actions.resetForm}>
              {() => (
                <>
                  <ScrollableUserList usersIds={userIds} />
                  {/* Error message */}
                  <Alert variant={status?.type} onClose={() => actions.setStatus()} show={!!status?.msg}>
                    {t(status?.msg, { count: status?.failedCount })}
                  </Alert>
                  <Select module="manager.attendances.form" name="requestStatusId" options={timeOffStatuses} required />
                  {/* Time off type */}
                  <Select
                    module="manager.timeOffs.form"
                    name="vacationTypeId"
                    options={timeOffTypes.map(reason => ({ ...reason, name: reason[`name${currentLang(true)}`] }))}
                    required
                    handleChange={({ target: { value } }) => {
                      const { partialDayByDefault, defaultStartTime, defaultEndTime } = timeOffTypesById[value]
                      if (!partialDayByDefault || !defaultStartTime || !defaultEndTime) return
                      actions.setFieldValue('start', setTime(baseDate, defaultStartTime))
                      actions.setFieldValue('end', setTime(baseDate, defaultEndTime))
                    }}
                  />
                  {/* Checkbox for partial dates */}

                  <Checkbox module="manager.timeOffs.form" name="partialDates" />

                  <DateTimePicker module="manager.timeOffs.form" type={values.partialDates ? 'dateTime' : 'date'} name="start" required />
                  <DateTimePicker module="manager.timeOffs.form" type={values.partialDates ? 'dateTime' : 'date'} name="end" required />

                  {/* Submit & Cancel buttons */}
                  <Button type="submit" isSubmitting={isSubmitting} text="SUBMIT" />
                  <Button className="btn btn-outline-secondary ml-1" text="actions.CANCEL" onClick={() => dispatch(closeModal())} />
                </>
              )}
            </Form>
          )
        }}
      </Formik>
    </BaseModal>
  )
}

export default TimeOffMultiCreateModal
